import { API_URL } from '@/config/app';

const DEFAULT_PUBLIC_ASSET_ORIGINS = [
  'http://103.173.204.67',
  'https://103.173.204.67',
  'http://180.179.212.100',
  'https://180.179.212.100',
];

const PUBLIC_ASSET_FOLDER_PATTERN =
  /^\/?(images|pdf_Samples|eBook_Samples|pdf)\//i;

function normalizeUploadPath(path: string): string {
  return path.replace(/\/api\/v\d+\/uploads\//, '/uploads/');
}

function configuredPublicAssetOrigins(): string[] {
  const fromEnv = String(import.meta.env.VITE_PUBLIC_ASSETS_ORIGIN ?? '')
    .split(',')
    .map((value) => value.trim().replace(/\/+$/, ''))
    .filter(Boolean)
    .map((value) => value.replace(/^(https?):(?!\/)/i, '$1://'));
  const apiOrigin = API_URL.replace(/\/api\/v\d+\/?$/, '').replace(/\/+$/, '');
  return [...new Set([...fromEnv, ...(apiOrigin ? [apiOrigin] : []), ...DEFAULT_PUBLIC_ASSET_ORIGINS])];
}

/**
 * HTTPS admin pages cannot load http://IP/... covers (mixed content).
 * Factbook trees live under API `uploads/` (same as media) — rewrite to that.
 */
function toApiPublicAssetProxyUrl(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Already a usable API uploads / proxy URL
  if (trimmed.startsWith('/asset-proxy/')) {
    return trimmed;
  }
  if (trimmed.includes('/public-assets/')) {
    return trimmed;
  }
  if (/\/uploads\/(images|pdf_Samples|eBook_Samples|pdf)\//i.test(trimmed)) {
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    if (trimmed.startsWith('/')) {
      return `${getApiOrigin()}${trimmed}`;
    }
  }

  let relative: string | undefined;

  for (const origin of configuredPublicAssetOrigins()) {
    if (trimmed === origin) continue;
    if (trimmed.startsWith(`${origin}/`)) {
      relative = trimmed.slice(origin.length + 1);
      break;
    }
  }

  if (!relative && PUBLIC_ASSET_FOLDER_PATTERN.test(trimmed)) {
    relative = trimmed.replace(/^\/+/, '');
  }

  if (!relative) return undefined;

  if (relative.toLowerCase().startsWith('uploads/')) {
    relative = relative.slice('uploads/'.length);
  }

  if (!PUBLIC_ASSET_FOLDER_PATTERN.test(relative)) {
    return undefined;
  }

  return `${getApiOrigin()}/uploads/${relative}`;
}

function resolveUploadUrl(path: string): string {
  const proxied = toApiPublicAssetProxyUrl(path);
  if (proxied) return proxied;

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return normalizeUploadPath(path);
  }

  if (path.startsWith('/')) {
    return `${getApiOrigin()}${normalizeUploadPath(path)}`;
  }

  return `${getApiOrigin()}/uploads/${path}`;
}

export function getApiOrigin(): string {
  const originFromApi = API_URL.replace(/\/api\/v\d+\/?$/, '');
  if (originFromApi) {
    return originFromApi;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return '';
}

export function getUserAvatarUrl(avatar?: string | null): string | undefined {
  if (!avatar) return undefined;

  if (avatar.startsWith('http://') || avatar.startsWith('https://') || avatar.startsWith('data:')) {
    return avatar;
  }

  return `${getApiOrigin()}/uploads/avatars/${avatar}`;
}

export function getCategoryImageUrl(image?: string | null): string | undefined {
  if (!image) return undefined;

  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
    return image;
  }

  return `${getApiOrigin()}/uploads/categories/${image}`;
}

export function getSubcategoryImageUrl(image?: string | null): string | undefined {
  if (!image) return undefined;

  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
    return image;
  }

  return `${getApiOrigin()}/uploads/subcategories/${image}`;
}

export function getAttributeValueImageUrl(image?: string | null): string | undefined {
  if (!image) return undefined;

  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
    return image;
  }

  return `${getApiOrigin()}/uploads/attribute-values/${image}`;
}

export function getBrandImageUrl(image?: string | null): string | undefined {
  if (!image) return undefined;

  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
    return image;
  }

  return `${getApiOrigin()}/uploads/brands/${image}`;
}

export function getBannerImageUrl(image?: string | null): string | undefined {
  if (!image) return undefined;

  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
    return image;
  }

  return `${getApiOrigin()}/uploads/banners/${image}`;
}

export function extractBannerImageFilename(value?: string | null): string {
  if (!value) return '';

  const trimmed = value.trim();
  const match = trimmed.match(/\/uploads\/banners\/([^/?#]+)$/);
  if (match) {
    return match[1];
  }

  return trimmed;
}

export function getCollectionImageUrl(image?: string | null): string | undefined {
  if (!image) return undefined;

  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
    return image;
  }

  return `${getApiOrigin()}/uploads/collections/${image}`;
}

export function getMediaImageUrl(image?: string | null): string | undefined {
  if (!image) return undefined;

  if (image.startsWith('data:')) {
    return image;
  }

  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('/')) {
    return resolveUploadUrl(image);
  }

  return `${getApiOrigin()}/uploads/media/${image}`;
}

export function resolveUserAvatarUrl(user?: { avatar?: string | null } | null): string | undefined {
  return getUserAvatarUrl(user?.avatar);
}

export function getCustomerImageUrl(image?: string | null): string | undefined {
  if (!image) return undefined;

  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
    return image;
  }

  return `${getApiOrigin()}/uploads/customers/${image}`;
}

export function resolveCustomerImageUrl(
  customer?: { profileImage?: string | null; avatar?: string | null } | null,
): string | undefined {
  return getUserAvatarUrl(customer?.profileImage ?? customer?.avatar);
}

export function getSamplePdfUrl(url?: string | null): string | undefined {
  if (!url?.trim()) return undefined;

  const trimmed = url.trim();
  const proxied = toApiPublicAssetProxyUrl(trimmed);
  if (proxied) return proxied;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return normalizeUploadPath(trimmed);
  }

  if (trimmed.startsWith('/')) {
    return resolveUploadUrl(trimmed);
  }

  return resolveUploadUrl(`products/samples/${trimmed}`);
}

export function getSamplePdfDisplayName(url?: string | null): string {
  if (!url?.trim()) return '';

  const fromPath = url.trim().split('/').pop() ?? '';
  if (!fromPath) return 'Sample for PRINT';
  if (/^[a-f0-9-]{36}\.pdf$/i.test(fromPath)) return 'Sample for PRINT';

  return decodeURIComponent(fromPath);
}

export function getDigitalSamplePdfUrl(url?: string | null): string | undefined {
  if (!url?.trim()) return undefined;

  const trimmed = url.trim();
  const proxied = toApiPublicAssetProxyUrl(trimmed);
  if (proxied) return proxied;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return normalizeUploadPath(trimmed);
  }

  if (trimmed.startsWith('/')) {
    return resolveUploadUrl(trimmed);
  }

  // Extracted HTML packages live under digital-samples; legacy single files under samples.
  if (trimmed.includes('/')) {
    return resolveUploadUrl(`products/digital-samples/${trimmed}`);
  }

  return resolveUploadUrl(`products/samples/${trimmed}`);
}

export function getDigitalSamplePdfDisplayName(url?: string | null): string {
  if (!url?.trim()) return '';

  const trimmed = url.trim();
  const packageMatch = trimmed.match(
    /\/uploads\/products\/digital-samples\/([a-f0-9-]{36})(?:\/|$)/i,
  );
  if (packageMatch) {
    const fileName = trimmed.split('/').pop() ?? 'index.html';
    return decodeURIComponent(fileName) || 'Sample for eFLIPBOOK and WEBBASED';
  }

  const fromPath = trimmed.split('/').pop() ?? '';
  if (!fromPath) return 'Sample for eFLIPBOOK and WEBBASED';
  if (/^[a-f0-9-]{36}\.(pdf|html|htm)$/i.test(fromPath)) {
    return 'Sample for eFLIPBOOK and WEBBASED';
  }
  if (/\.(zip|rar)$/i.test(fromPath)) return decodeURIComponent(fromPath);

  return decodeURIComponent(fromPath);
}

export function getContentPdfUrl(url?: string | null): string | undefined {
  if (!url?.trim()) return undefined;

  const trimmed = url.trim();
  const proxied = toApiPublicAssetProxyUrl(trimmed);
  if (proxied) return proxied;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return normalizeUploadPath(trimmed);
  }

  if (trimmed.startsWith('/')) {
    return resolveUploadUrl(trimmed);
  }

  return resolveUploadUrl(`products/content/${trimmed}`);
}

export function getContentPdfDisplayName(url?: string | null): string {
  if (!url?.trim()) return '';

  const fromPath = url.trim().split('/').pop() ?? '';
  if (!fromPath) return 'Content PDF';
  if (/^[a-f0-9-]{36}\.pdf$/i.test(fromPath)) return 'Content PDF';

  return decodeURIComponent(fromPath);
}
